import { clamp } from 'lodash';
import { VesselArea, VesselSpeedType } from '../../graphql';
import { dToMs, hToMs, msToH } from '../../lib/time';
import {
  assertedCast,
  exhaustiveCheck,
  findIndexWith,
  get,
  groupBySequence,
  isDefined,
  mapKeyBy,
  memoize,
  setFind,
  withDefined,
} from '../../lib/util';
import { DEFAULT_CARBON_FACTOR } from '../pnl/co2/euEts';
import type { LocationId, OmitFunctions, ScheduleData } from './Core';
import {
  calculateSeaDuration,
  calculateWorkShifts,
  ceilIfEnabled,
  DELAY_CONSTRAINT_HOURS,
  determinePortArea,
  determineVesselArea,
  fallbackBallastSpeed,
  fallbackLadenSpeed,
  getDefaultVesselSpeedByType,
  getEventKey,
  getEventLocation,
  getEventOrder,
  getOrderLaytimeDays,
  isPortCallEventStartSourced,
} from './Core';
import {
  getEffectiveCargoHandlingRate,
  getLoadingTime,
} from './Core.loading-discharging';
import type { Vessel } from './Vessel';
import { getVesselBannedChannels, getVesselCapacities } from './Vessel';
import { getAssortmentExclusionsByShipmentId } from './assortmentExclusions';
import { minimumEventDuration } from '@seaber/common/core/entries';
import { getNearestSpeedConsumption } from './Core.bunker-consumption';
import type { LoadedCompartment } from './Core.cargo-allocation';
import {
  getAndAddLoadedAmountToCompartments,
  getCompartments,
  getMaxAllowedLoadAmount,
  getOverlappingLoadsByOrder,
  getVoyageExclusionsById,
  isCorrectCargoAllocationType,
  releaseOrderFromCompartments,
} from './Core.cargo-allocation';
import type {
  CalculationConfig,
  ScheduleDataContext,
} from './ScheduleDataContext';
import type {
  CalculatedWork,
  CargoEvent,
  EnrichedSpeedConsumption,
  Events,
  TimeData,
} from './TimeData';
import {
  canEventBeConcurrent,
  CoreEventType,
  getEventEndLocationId,
  getEventLocationId,
  getEventStartLocationId,
  isCargoEvent,
  isCargoEventTimeData,
  isDeckCargo,
  isDischargeEvent,
  isEventConcurrent,
  isLoadEvent,
  isPortCallArrivalEvent,
  isPortCallDepartureEventTimeData,
  isPortCallEvent,
  isPortCallEventTimeData,
  isSingleEvent,
  SingleEventType,
  isTcInEvent,
} from './TimeData';
import type { DeriveCapacitiesAndTimesState } from './TimeDataCache';
import {
  cacheTimeData,
  cloneDeriveCapacitiesAndTimesState,
  getCachedTimeData,
} from './TimeDataCache';
import { stringToNumOpt } from './util';
import type { PortCostEstimateMaps } from '../hooks/usePortCostEstimates';
import { getDefaultPortCostByType } from '../hooks/usePortCostEstimates';
import { getEventShipmentId } from '../shipment/shipment';
import { getHistoricalPortCostEstimates } from '../shipment/costs/portCostHistory';
import { getMaxDefaultPortCostForLocation } from '../shipment/costs/portCostDefault';
import type { PortCostPriority } from '../shipment/costs/costs';
import {
  DEFAULT_PORT_COST_PRIORITY,
  portCostPriorityKeys,
} from '../shipment/costs/costs';
import { type ChannelCostMatchCriteria } from '../pnl/channelCost/channelCostIndex';
import { getEffectiveChannelCost } from '../pnl/channelCost/channelCostUtils';
import {
  ChannelCostCargoCondition,
  PortType,
} from '@seaber/common/graphql/types';
import { mapGetFirstEntry } from '@seaber/common/lib/collections';
import { getLatestHistoricalPortCost } from '../shipment/costs/portCostLatestHistory';

/** Constant params required for timeline derivation, collected into an object for easier parameter drilling */
export type DeriveCapacitiesAndTimesParams = {
  vessel: Vessel;
  context: Pick<
    ScheduleDataContext,
    'coasById' | 'distances' | 'portCostEstimateMaps'
  >;
  config: OmitFunctions<CalculationConfig>;
  now?: number;
};

export type DeriveCapacitiesAndTypesOptions = {
  skipSwapComputation?: boolean;
  calculateCosts: boolean;
  useDbValues?: boolean;
  writeTimeDataCache?: boolean;
  /**
   * The location the vessel was at before the first event.
   * If provided, DCAT will calculate sea distance from this location to the first event.
   * If not provided, DCAT assumes the vessel starts at the first event's location.
   */
  initialLocationId?: LocationId;
};

/**
 * Calculate movable mass, amount of time required, buffers and violations and costs
 */
export const DeriveCapacitiesAndTimes = <EventType extends Events>(
  vessel: DeriveCapacitiesAndTimesParams['vessel'],
  events: EventType[],
  // TODO Where should we take initial location and start time / first event to consider ?
  // TODO Last event that has already happened / started ?
  {
    coasById,
    distances,
    portCostEstimateMaps,
    voyageExclusions,
  }: Pick<
    ScheduleDataContext,
    | 'coasById'
    | 'distances'
    | 'portCostEstimateMaps'
    | 'voyageExclusions'
    | 'locations'
    | 'currentUser'
  >,
  config: OmitFunctions<CalculationConfig>,
  {
    skipSwapComputation = false,
    useDbValues = false,
    calculateCosts,
    writeTimeDataCache = false,
    initialLocationId,
  }: DeriveCapacitiesAndTypesOptions,
  now: number = Date.now(),
): ScheduleData<EventType> => {
  const {
    massCapacity: weightCapacity,
    volumeCapacity,
    deckVolumeCapacity,
  } = getVesselCapacities(vessel);
  const tcRate = Number(vessel.tcRate ?? 0) / 24;

  const bannedChannels = getVesselBannedChannels(vessel);

  const firstEvent: Events | undefined = events[0];

  const speedLaden = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.LADEN,
    fallbackLadenSpeed,
  );
  const speedBallast = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.BALLAST,
    fallbackBallastSpeed,
  );

  const firstRealEventIndex = findIndexWith(events, 0, 1, (e) => !e.skipOnSwap);
  const lastRealEventIndex = findIndexWith(
    events,
    events.length - 1,
    -1,
    (e) => !e.skipOnSwap,
  );
  if (
    (firstRealEventIndex === undefined || lastRealEventIndex === undefined) &&
    events.length > 0
  ) {
    console.warn(`Event batch doesn't have any real, non-skippable events`);
  }

  // Get initial items from cache
  const {
    startIndex,
    timeData: arr,
    state: cachedState,
  } = getCachedTimeData<EventType>(events);

  // Collect any mutated values to state so we may cache and restore it
  const state: DeriveCapacitiesAndTimesState = cachedState ?? {
    // Use initialLocationId if provided, otherwise assume vessel starts at first event's location
    prev:
      initialLocationId ??
      withDefined(firstEvent, getEventStartLocationId) ??
      (-1 as LocationId),
    portCallArrivalIndex: null,
    /** Start time of the first event in the current port call. The same value for every event in the same port call. */
    arriveInPort: firstEvent?.startOverride ?? firstEvent?.start ?? now,
    previousEnd: firstEvent?.startOverride ?? firstEvent?.start ?? now,
    volumeCapacityAvailable: volumeCapacity,
    deckVolumeCapacityAvailable: deckVolumeCapacity,
    massCapacityAvailable: weightCapacity,
    loadedCompartmentsById: mapKeyBy(
      getCompartments(vessel).map(
        (c): LoadedCompartment => ({
          compartment: c,
          allocations: [],
          assortment: null,
        }),
      ),
      (c) => c.compartment.id,
    ),
    totalHoursAtSea: 0,
    totalHoursAtPort: 0,
    ballastDistance: 0,
    totalDistance: 0,
    bunkerCost: 0,
    portCostEstimateTotal: 0,
    totalMass: 0,
    delays: 0,
    constrainedDelays: 0,
    tcCostTotal: 0,
    cost: 0,
    orderToLoadIndex: new Map<number, number>(),
    hasFirstShipmentEvent: false,
    concurrentPortHours: 0,
    currentPortCallOperationsStart: null,
    ordersOnBoard: 0,
    carriedAssortmentsByOrderId: new Map(),
  };

  const {
    assortmentExclusions,
    bunkerPriceByGradeAndPortCode,
    orgDefaultBunkerGradeIdsByArea,
    bunkerGradeCalculations,
    resolvedBunkerGrades,
  } = config;

  const assortmentExclusionsByShipmentId = getAssortmentExclusionsByShipmentId(
    vessel,
    events,
    assortmentExclusions,
  );

  const overlappingLoadsByOrder = getOverlappingLoadsByOrder(events);

  const voyageExclusionsById = getVoyageExclusionsById(voyageExclusions);

  for (let i = startIndex, l = events.length; i < l; ++i) {
    const event = events[i];
    const prevTd = get(arr, i - 1);
    const nextEvent = get(events, i + 1);

    const isLastEvent = i === lastRealEventIndex;
    const isFirstEvent = i === firstRealEventIndex;

    const laden = state.ordersOnBoard > 0;

    const {
      constraints,
      startOverride,
      durationOverride,
      seaDurationOverride: savedSeaDurationOverride,
      sofCommenced,
      sofDuration,
      category,
      type,
    } = event;

    const inputs: TimeData['inputs'] = {};

    const startLocationId = getEventStartLocationId(event);
    const endLocationId = getEventEndLocationId(event);
    const isPortCallArrival = isPortCallArrivalEvent(event);
    let avoidsChannels =
      (isPortCallArrival && event.legAvoidsChannels) ?? false;

    if (prevTd && isPortCallDepartureEventTimeData(prevTd)) {
      state.portCallArrivalIndex = null;
    }
    if (isPortCallArrival) {
      state.portCallArrivalIndex = i;
    }

    /** The PortCallArrivalEvent of the current port call */
    const currentPortCallArrival =
      state.portCallArrivalIndex === null
        ? null
        : assertedCast(
            events[state.portCallArrivalIndex],
            isPortCallArrivalEvent,
          );

    /** Did the location change between the previous and current event **/
    const changeLocation = state.prev !== -1 && startLocationId !== state.prev;

    const routeWithChannels = changeLocation
      ? distances.get(state.prev, startLocationId, false)
      : undefined;

    const bannedChannelsInRoute = (
      routeWithChannels?.used_channels ?? []
    ).filter((c) => bannedChannels.has(c.toLowerCase()));

    if (bannedChannelsInRoute.length > 0) {
      avoidsChannels = true;
    }

    const routeData = changeLocation
      ? distances.get(state.prev, startLocationId, avoidsChannels)
      : undefined;
    const distance = routeData?.dist || 0;
    state.totalDistance += distance;

    if (!laden) {
      state.ballastDistance += distance;
    }

    let portCostEstimate = 0;
    let channelCost = 0;
    /** Hours */
    let channelDurationOverhead = 0;

    let extraPortHours = 0;
    let extraPortDuration = 0;
    const isPortArrivalForSuggestions =
      !isPortCallArrival && (changeLocation || i == 0);
    const isCanalTransitPortCall =
      isPortCallEvent(event) && event.isCanalTransitPortCall;
    if (isPortArrivalForSuggestions) {
      const location = getEventLocation(event, config.locationsById);
      extraPortHours = (stringToNumOpt(location?.extraPortDays) || 0) * 24;
      extraPortDuration = hToMs(extraPortHours);
    }
    if (
      calculateCosts &&
      vessel.name &&
      (changeLocation || i == 0) &&
      portCostEstimateMaps &&
      type !== CoreEventType.OffHire
    ) {
      if (!isCanalTransitPortCall) {
        const portCallId = isPortCallEvent(event) ? event.portCallId : null;
        const portType = getPortTypeFromEvents(events, portCallId);
        const portCost = getEffectivePortCost(
          event,
          portCostEstimateMaps,
          config.portCostPriority,
          vessel as typeof vessel & { name: string }, // Name asserted above
          config.locationsById,
          portType,
        );
        state.portCostEstimateTotal += portCost;
        portCostEstimate = portCost;
      }

      if (routeData?.used_channels && routeData.used_channels.length > 0) {
        const channelCostResult = getChannelCost(
          event,
          routeData.used_channels,
          laden,
          state.carriedAssortmentsByOrderId,
          vessel,
          portCostEstimateMaps,
          config.vesselAttributeById,
          now,
        );
        channelCost = channelCostResult.channelCost ?? 0;
        channelDurationOverhead = channelCostResult.channelDurationOverhead;
      }
    }

    const isFirstShipmentEvent: boolean =
      !state.hasFirstShipmentEvent &&
      type !== CoreEventType.OffHire &&
      type !== CoreEventType.Static;

    const isConcurrentWithPrevious =
      startLocationId === state.prev &&
      isEventConcurrent(event) &&
      prevTd &&
      canEventBeConcurrent(prevTd.event);

    const eventStartIsSourced =
      isPortCallEvent(event) && isPortCallEventStartSourced(event);

    /** Ensure that origin is the same as it was when recording sea duration value */
    const seaDurationIsValid =
      !isPortCallArrivalEvent(event) ||
      !state.prev ||
      !event.previousLocationId ||
      event.previousLocationId === state.prev;

    const seaDurationOverride =
      savedSeaDurationOverride === null || !seaDurationIsValid
        ? null
        : eventStartIsSourced && startOverride
          ? clamp(
              savedSeaDurationOverride,
              minimumEventDuration,
              startOverride - state.previousEnd,
            )
          : savedSeaDurationOverride;

    /** Trivial when sea duration override is set. When no explicit seaDurationOverride, if there is a start override then we override the sea duration with the time difference. */
    const effectiveSeaDurationOverride =
      seaDurationOverride ??
      (isPortCallEvent(event) && startOverride
        ? Math.max(1, startOverride - state.previousEnd)
        : null);

    /** Sea duration from previous event. Always 0 between events in the same port call. */
    const calculatedSeaDuration = useDbValues
      ? Math.max(0, event.start - state.previousEnd)
      : calculateSeaDuration(
          speedLaden,
          speedBallast,
          distance,
          laden,
          config.useRoundedEstimations,
        ) + hToMs(channelDurationOverhead);

    const isEtaOverride =
      changeLocation && effectiveSeaDurationOverride !== null;
    /** Sea duration if this event is the first one in this port. 0 for all other events. */
    let hoursAtSea = isEtaOverride
      ? msToH(effectiveSeaDurationOverride)
      : msToH(calculatedSeaDuration);

    if (changeLocation) {
      state.arriveInPort = useDbValues
        ? event.start
        : state.previousEnd +
          (effectiveSeaDurationOverride ?? hToMs(hoursAtSea));
      state.currentPortCallOperationsStart = null;
    }

    /** Current running time, earliest possible placement for current event */
    const arriveToEvent =
      isConcurrentWithPrevious && prevTd
        ? prevTd.start
        : useDbValues
          ? event.start
          : state.previousEnd +
            ((changeLocation && effectiveSeaDurationOverride) ||
              hToMs(hoursAtSea)) +
            (isPortArrivalForSuggestions ? extraPortDuration : 0);

    const { layday, rta } = constraints;

    /** The time this event can actually start considering override and running time */
    const startPortActivity =
      type === CoreEventType.OffHire
        ? event.start
        : startOverride
          ? isFirstShipmentEvent
            ? startOverride
            : Math.max(arriveToEvent, startOverride)
          : Math.max(arriveToEvent, event.delayUntil ?? 0);

    /** If this event's start override is sooner than possible (before previous event ended) */
    const hoursDelayConstraint = startOverride
      ? msToH(startPortActivity - arriveToEvent)
      : 0;

    /** Hours */
    let cancellingOrDeliveryViolationVsEta;
    /** Hours */
    let cancellingOrDeliveryViolationVsOperations;
    /** Hours */
    let cancellingOrDeliveryViolationVsEvent;
    let hasMiddle = false;
    let calculatedDuration: number;
    let work: CalculatedWork | undefined = undefined;
    let duration: number;
    let amount = 1;

    let start: number;

    const nextEventStartOverride = nextEvent?.startOverride ?? null;

    /** Actual mass loaded considering vessel capacity */
    let massLoaded = 0;
    /** Actual volume loaded considering vessel capacity */
    let volumeLoaded = 0;
    /** Cargo volume stowed on hold */
    let holdVolume = 0;
    /** Cargo volume stowed on deck */
    let deckVolume = 0;

    let pcs = 0;
    let cleaningCost = 0;

    const deckCargo =
      isLoadEvent(event) || isDischargeEvent(event)
        ? isDeckCargo(event.order.orderDetailsByOrderId.nodes)
        : false;

    if (isLoadEvent(event)) {
      const { order } = event;

      state.ordersOnBoard++;

      if (order.assortmentId != null) {
        state.carriedAssortmentsByOrderId.set(order.id, {
          assortmentId: order.assortmentId,
          assortmentGroupId:
            order.assortmentByAssortmentId?.assortmentGroupByAssortmentGroup
              ?.id ?? null,
        });
      }

      start = Math.max(sofCommenced ?? 0, startPortActivity);
      if (state.currentPortCallOperationsStart === null) {
        state.currentPortCallOperationsStart = start;
      }
      const cargoRange = order.cargoRanges;
      const maxMass = cargoRange.mass.max;
      const maxVolume = cargoRange.volume.max;

      const hasMatchingCompartment = !!setFind(
        state.loadedCompartmentsById,
        (c) =>
          isCorrectCargoAllocationType(
            order.assortmentByAssortmentId?.cargoType ?? null,
            c.compartment,
          ),
      );

      const totalVolumeByAllocations =
        state.loadedCompartmentsById.size == 0
          ? // Client doesn't use compartments on this vessel, assume all fits
            maxVolume
          : !hasMatchingCompartment
            ? // Compartments defined but wrong type, nothing fits
              0
            : !event.isNew &&
                !event.cargoAllocations?.some((a) => +a.amount > 0)
              ? // Allocations not defined (yet?), assume all fits (helps legacy data, also allows overriding tank logic when necessary)
                Infinity
              : getAndAddLoadedAmountToCompartments(
                  order.assortmentByAssortmentId,
                  event.cargoAllocations ?? [],
                  state.loadedCompartmentsById,
                );

      state.orderToLoadIndex.set(order.id, i);
      const maxAllowedAmount = getMaxAllowedLoadAmount(
        event,
        {
          mass: state.massCapacityAvailable,
          volume: deckCargo
            ? state.deckVolumeCapacityAvailable
            : state.volumeCapacityAvailable,
        },
        overlappingLoadsByOrder.get(order.id) ?? [],
      );
      amount =
        maxMass === 0 || maxVolume === 0
          ? 1
          : Math.min(
              maxAllowedAmount,
              state.massCapacityAvailable / maxMass,
              deckCargo
                ? state.deckVolumeCapacityAvailable / maxVolume
                : state.volumeCapacityAvailable / maxVolume,
              (totalVolumeByAllocations ?? Infinity) / maxVolume,
              1,
            );
      massLoaded = maxMass * amount;
      volumeLoaded = maxVolume * amount;
      holdVolume = deckCargo ? 0 : volumeLoaded;
      deckVolume = deckCargo ? volumeLoaded : 0;
      state.totalMass += massLoaded;
      state.massCapacityAvailable -= massLoaded;
      state.volumeCapacityAvailable -= holdVolume;
      state.deckVolumeCapacityAvailable -= deckVolume;

      pcs += event.pcs;

      const {
        rate: loadingRate,
        unit: loadingUnit,
        source: loadingRateSource,
      } = getEffectiveCargoHandlingRate(
        order,
        event.entry,
        vessel,
        coasById,
        config,
        'load',
      );

      work = calculateWorkShifts(
        order.locationByLoadPortId,
        start,
        getLoadingTime(
          massLoaded,
          volumeLoaded,
          loadingRate,
          loadingUnit,
          msToH(Number(order.loadingOverheadDuration ?? 0)),
        ),
        getOrderLaytimeDays('loading', order, coasById),
      );
      calculatedDuration = hToMs(
        ceilIfEnabled(work.duration, config.useRoundedEstimations),
      );

      inputs.loadingRate = {
        value: loadingRate,
        unit: loadingUnit,
        source: loadingRateSource,
      };

      duration =
        durationOverride != null
          ? durationOverride
          : nextEventStartOverride
            ? Math.min(nextEventStartOverride - start - 1, calculatedDuration)
            : calculatedDuration;

      const { cancelling } = constraints;

      if (layday && state.arriveInPort < layday) {
        cancellingOrDeliveryViolationVsEta = msToH(state.arriveInPort - layday);
      } else if (cancelling && state.arriveInPort > cancelling) {
        cancellingOrDeliveryViolationVsEta = msToH(
          state.arriveInPort - cancelling,
        );
      } else {
        cancellingOrDeliveryViolationVsEta = 0;
      }
      if (layday && start < layday) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - layday);
      } else if (cancelling && start > cancelling) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - cancelling);
      } else {
        cancellingOrDeliveryViolationVsEvent = 0;
      }
      if (layday && state.currentPortCallOperationsStart < layday) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - layday,
        );
      } else if (
        cancelling &&
        state.currentPortCallOperationsStart > cancelling
      ) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - cancelling,
        );
      } else {
        cancellingOrDeliveryViolationVsOperations = 0;
      }
    } else if (isDischargeEvent(event)) {
      const { order } = event;

      state.ordersOnBoard--;

      if (order.assortmentId != null) {
        state.carriedAssortmentsByOrderId.delete(order.id);
      }

      start = Math.max(sofCommenced ?? 0, startPortActivity);
      if (state.currentPortCallOperationsStart === null) {
        state.currentPortCallOperationsStart = start;
      }
      const loadIndex = state.orderToLoadIndex.get(order.id);
      const indexDistance = i - (loadIndex ?? 0);
      if (loadIndex === undefined || indexDistance < 1) {
        throw new Error(
          `Invalid port rotation: Order ${order.id} discharged before loading`,
        );
      }
      hasMiddle =
        indexDistance > 1 &&
        findIndexWith(events, loadIndex + 1, 1, (e) => !e.skipOnSwap, i - 1) !==
          undefined;
      const loadEvent = arr[loadIndex];
      loadEvent.hasMiddle = hasMiddle;
      amount = loadEvent.amount;
      massLoaded = loadEvent.mass;
      volumeLoaded = loadEvent.volume;
      holdVolume = deckCargo ? 0 : volumeLoaded;
      deckVolume = deckCargo ? volumeLoaded : 0;
      state.massCapacityAvailable += massLoaded;
      state.volumeCapacityAvailable += holdVolume;
      state.deckVolumeCapacityAvailable += deckVolume;

      pcs += event.pcs;
      releaseOrderFromCompartments(order.id, state.loadedCompartmentsById);

      const {
        rate: dischargeRate,
        unit: dischargeUnit,
        source: dischargeRateSource,
      } = getEffectiveCargoHandlingRate(
        order,
        event.entry,
        vessel,
        coasById,
        config,
        'discharge',
      );

      work = calculateWorkShifts(
        order.locationByDischargePortId,
        start,
        getLoadingTime(
          massLoaded,
          volumeLoaded,
          dischargeRate,
          dischargeUnit,
          msToH(Number(order.dischargingOverheadDuration ?? 0)),
        ),
        getOrderLaytimeDays('discharge', order, coasById),
      );
      calculatedDuration = hToMs(
        ceilIfEnabled(work.duration, config.useRoundedEstimations),
      );

      inputs.dischargeRate = {
        value: dischargeRate,
        unit: dischargeUnit,
        source: dischargeRateSource,
      };

      duration =
        durationOverride != null
          ? durationOverride
          : nextEventStartOverride
            ? Math.min(nextEventStartOverride - start - 1, calculatedDuration)
            : calculatedDuration;

      const { deliveredBefore } = constraints;
      if (rta && state.arriveInPort < rta) {
        cancellingOrDeliveryViolationVsEta = msToH(state.arriveInPort - rta);
      } else if (deliveredBefore && state.arriveInPort > deliveredBefore) {
        cancellingOrDeliveryViolationVsEta = msToH(
          state.arriveInPort - deliveredBefore,
        );
      } else {
        cancellingOrDeliveryViolationVsEta = 0;
      }
      if (rta && start < rta) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - rta);
      } else if (deliveredBefore && start > deliveredBefore) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - deliveredBefore);
      } else {
        cancellingOrDeliveryViolationVsEvent = 0;
      }
      if (rta && state.currentPortCallOperationsStart < rta) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - rta,
        );
      } else if (
        deliveredBefore &&
        state.currentPortCallOperationsStart > deliveredBefore
      ) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - deliveredBefore,
        );
      } else {
        cancellingOrDeliveryViolationVsOperations = 0;
      }
    } else if (type === CoreEventType.Single) {
      start = startPortActivity;
      calculatedDuration = Math.max(event.duration, minimumEventDuration);
      duration = event.duration;
      cancellingOrDeliveryViolationVsEta = 0; // Single events (& Port calls) don't have laycan/delivery date and hence cannot violate it
      cancellingOrDeliveryViolationVsOperations = 0; // Single events (& Port calls) don't have laycan/delivery date and hence cannot violate it
      cancellingOrDeliveryViolationVsEvent = 0; // Single events (& Port calls) don't have laycan/delivery date and hence cannot violate it

      const exclusion = voyageExclusionsById.get(
        event.cleaningAssortmentExclusionId,
      );
      if (exclusion) {
        cleaningCost = Number(exclusion.holdCleaningCost) ?? 0;

        // Check if the duration is outdated and needs updating based on exclusion
        const expectedDuration = hToMs(Number(exclusion.holdCleaningTimeHours));
        if (expectedDuration !== duration) {
          calculatedDuration = Math.max(expectedDuration, minimumEventDuration);
          duration = calculatedDuration;
        }
      }

      const arrival = get(events, i - 2);
      const { portCallId } = event;
      if (
        arrival &&
        portCallId &&
        isPortCallArrivalEvent(arrival) &&
        portCallId === arrival.portCallId
      ) {
        const arrivalTd = arr[i - 2];
        hoursAtSea = arrivalTd.hoursAtSea;
      }
    } else if (type === CoreEventType.OffHire) {
      start = event.start;
      calculatedDuration = event.end - event.start;
      duration = calculatedDuration;
      const arrivalAndStartDelta = arriveToEvent - start;

      cancellingOrDeliveryViolationVsEta = msToH(
        Math.max(arrivalAndStartDelta, 0),
      );
      cancellingOrDeliveryViolationVsOperations = msToH(
        Math.max(startPortActivity - start, 0),
      );
      cancellingOrDeliveryViolationVsEvent = cancellingOrDeliveryViolationVsEta;
    } else if (type === CoreEventType.Static) {
      if (!isTcInEvent(event)) {
        exhaustiveCheck(
          event,
          'DeriveCapacitiesAndTimes only supports static events of type TC In',
        );
      }

      start = Math.max(arriveToEvent, event.start);
      // When determining the duration we ensure that the event is visible even
      // when it's pushed past it's defined end date by the previous event
      calculatedDuration = Math.max(event.end - start, dToMs(1));
      duration = calculatedDuration;

      const redeliveryEndDate = event.entry.redelivery?.endDate;
      const redeliveryViolation = redeliveryEndDate
        ? Math.max(msToH(arriveToEvent - redeliveryEndDate), 0)
        : 0;

      cancellingOrDeliveryViolationVsEta = redeliveryViolation;
      cancellingOrDeliveryViolationVsOperations = redeliveryViolation;
      cancellingOrDeliveryViolationVsEvent = redeliveryViolation;
    } else {
      throw new Error(`Unexpected event type, ${category} / ${type}`);
    }

    const hoursWaitingOrRtaBuffer = msToH(start - arriveToEvent);

    if (isConcurrentWithPrevious && prevTd) {
      start = Math.max(start, prevTd.start + 1);
    }

    duration = Math.max(minimumEventDuration, duration);

    // If there is a factual end given take it into consideration and recalculate duration.
    const sofEnd =
      sofCommenced && sofDuration ? sofCommenced + sofDuration : null;
    const end = Math.max(
      start + minimumEventDuration,
      Math.min(start + duration, sofEnd ?? Infinity),
    );
    duration = end - start;

    const signedDelay = cancellingOrDeliveryViolationVsEta;
    const delay = Math.abs(signedDelay);
    const constrainedDelay = Math.min(DELAY_CONSTRAINT_HOURS, delay); // Consider at most 30 days delay (30d * 24h = 720h)

    state.delays += delay;
    state.constrainedDelays += constrainedDelay;

    const hoursAtPort = msToH(end - arriveToEvent);
    const usedSpeed = distance / hoursAtSea;

    let insideEcaData: EnrichedSpeedConsumption | undefined = undefined;
    let insideEcaDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;
    let outsideEcaData: EnrichedSpeedConsumption | undefined = undefined;
    let outsideEcaDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;
    let bunkerPortData: EnrichedSpeedConsumption | undefined = undefined;
    let bunkerPortDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;
    let extraConsumptionData: EnrichedSpeedConsumption | undefined = undefined;
    let extraConsumptionDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;

    const insideEcaDistance = routeData?.inside_nm ?? 0;
    const outsideEcaDistance = routeData?.outside_nm ?? 0;

    const insideEcaHours = routeData?.inside_nm
      ? routeData.inside_nm / usedSpeed
      : 0;

    const outsideEcaHours = routeData?.outside_nm
      ? routeData.outside_nm / usedSpeed
      : 0;

    const area = determineVesselArea(routeData);
    const portCallEntry = currentPortCallArrival?.entry ?? null;
    // Attached to the port call entry
    const bunkerOverrides = portCallEntry?.bunkerOverrides ?? null;
    const weatherFactor = isPortCallArrivalEvent(event)
      ? event.weatherFactor
      : undefined;

    const commonProps = {
      bunkerPriceByGradeAndPortCode,
      vessel,
      currencyRates: config.currencyRates,
      portBunkerOverrides: bunkerOverrides,
      orgDefaultBunkerGradeIdsByArea,
      weatherFactor,
      bunkerGradeCalculations,
      resolvedBunkerGrades,
    };
    const nonPortCommonProps = {
      ...commonProps,
      targetSpeed: usedSpeed,
      type: laden ? VesselSpeedType.LADEN : VesselSpeedType.BALLAST,
    };
    const portCommonProps = {
      ...commonProps,
      distance: 0,
      targetSpeed: 0,
      type: VesselSpeedType.PORT,
    };

    if (calculateCosts && insideEcaDistance > 0) {
      const result = getNearestSpeedConsumption({
        vesselArea: VesselArea.INSIDE_ECA,
        calculatedHours: insideEcaHours,
        distance: insideEcaDistance,
        ...nonPortCommonProps,
      });
      if (result) {
        insideEcaData = result.primary;
        insideEcaDataSecondary = result.secondary;
      }
    }
    if (calculateCosts && outsideEcaDistance > 0) {
      const result = getNearestSpeedConsumption({
        vesselArea: VesselArea.OUTSIDE_ECA,
        distance: outsideEcaDistance,
        calculatedHours: outsideEcaHours,
        ...nonPortCommonProps,
      });
      if (result) {
        outsideEcaData = result.primary;
        outsideEcaDataSecondary = result.secondary;
      }
    }

    // For distance overrides inside/outside eca distance is not currently available
    if (
      calculateCosts &&
      !insideEcaData &&
      !outsideEcaData &&
      routeData?.is_override
    ) {
      const result = getNearestSpeedConsumption({
        vesselArea: VesselArea.ALL_AREAS,
        calculatedHours: hoursAtSea,
        distance: distance,
        ...nonPortCommonProps,
      });
      if (result) {
        outsideEcaData = result.primary;
        outsideEcaDataSecondary = result.secondary;
      }
    }

    if (calculateCosts && hoursAtPort > 0) {
      const portArea = determinePortArea(
        isPortCallEvent(event) ? event.location : null,
      );
      const result = getNearestSpeedConsumption({
        vesselArea: portArea,
        calculatedHours: hoursAtPort,
        isLoading: isLoadEvent(event),
        isCargoEvent: isCargoEvent(event),
        ...portCommonProps,
      });
      if (result) {
        bunkerPortData = result.primary;
        bunkerPortDataSecondary = result.secondary;
      }
    }

    const extraPortConsumptionHours =
      (insideEcaData?.idleHours ?? 0) +
      (outsideEcaData?.idleHours ?? 0) +
      extraPortHours;

    if (calculateCosts && extraPortConsumptionHours > 0) {
      const portArea = determinePortArea(
        isPortCallEvent(event) ? event.location : null,
      );
      const result = getNearestSpeedConsumption({
        vesselArea: portArea,
        calculatedHours: extraPortConsumptionHours,
        isLoading: false,
        isCargoEvent: false,
        isExtraPortConsumptionHours: true,
        ...portCommonProps,
      });
      if (result) {
        extraConsumptionData = result.primary;
        extraConsumptionDataSecondary = result.secondary;
      }
    }

    const portCostHours = isConcurrentWithPrevious
      ? Math.max(0, hoursAtPort - state.concurrentPortHours)
      : hoursAtPort;
    state.concurrentPortHours = isConcurrentWithPrevious
      ? Math.max(hoursAtPort, state.concurrentPortHours)
      : hoursAtPort;
    const bunkerCostInside = Number(insideEcaData?.voyageCost ?? 0);
    const bunkerCostInsideSecondary = Number(
      insideEcaDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostOutside = Number(outsideEcaData?.voyageCost ?? 0);
    const bunkerCostOutsideSecondary = Number(
      outsideEcaDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostPort = Number(bunkerPortData?.voyageCost ?? 0);
    const bunkerCostPortSecondary = Number(
      bunkerPortDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostExtra = Number(extraConsumptionData?.voyageCost ?? 0);
    const bunkerCostExtraSecondary = Number(
      extraConsumptionDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostTotal =
      bunkerCostInside +
      bunkerCostInsideSecondary +
      bunkerCostOutside +
      bunkerCostOutsideSecondary +
      bunkerCostPort +
      bunkerCostPortSecondary +
      bunkerCostExtra +
      bunkerCostExtraSecondary;
    state.bunkerCost += bunkerCostTotal;

    const seaCost = calculateCosts ? hoursAtSea * tcRate : 0;
    const portCost = calculateCosts ? portCostHours * tcRate : 0;

    const tcCost = seaCost + portCost;
    const voyageCost = bunkerCostTotal + portCostEstimate + channelCost;

    const total = voyageCost + tcCost;

    state.cost += total;
    state.tcCostTotal += tcCost;
    state.totalHoursAtSea += hoursAtSea;
    state.totalHoursAtPort += portCostHours;

    const totalConsumedBunkers =
      (insideEcaData?.consumedBunker ?? 0) +
      (insideEcaDataSecondary?.consumedBunker ?? 0) +
      (outsideEcaData?.consumedBunker ?? 0) +
      (outsideEcaDataSecondary?.consumedBunker ?? 0) +
      (bunkerPortData?.consumedBunker ?? 0) +
      (bunkerPortDataSecondary?.consumedBunker ?? 0) +
      (extraConsumptionData?.consumedBunker ?? 0) +
      (extraConsumptionDataSecondary?.consumedBunker ?? 0);

    let totalCO2 = 0;
    if (insideEcaData) {
      const carbonFactor = insideEcaData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * insideEcaData.consumedBunker;
    }
    if (insideEcaDataSecondary) {
      const carbonFactor =
        insideEcaDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * insideEcaDataSecondary.consumedBunker;
    }

    if (outsideEcaData) {
      const carbonFactor = outsideEcaData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * outsideEcaData.consumedBunker;
    }
    if (outsideEcaDataSecondary) {
      const carbonFactor =
        outsideEcaDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * outsideEcaDataSecondary.consumedBunker;
    }

    if (bunkerPortData) {
      const carbonFactor = bunkerPortData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * bunkerPortData.consumedBunker;
    }
    if (bunkerPortDataSecondary) {
      const carbonFactor =
        bunkerPortDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * bunkerPortDataSecondary.consumedBunker;
    }

    if (extraConsumptionData) {
      const carbonFactor =
        extraConsumptionData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * extraConsumptionData.consumedBunker;
    }
    if (extraConsumptionDataSecondary) {
      const carbonFactor =
        extraConsumptionDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * extraConsumptionDataSecondary.consumedBunker;
    }

    const key = getEventKey(event);

    // Assortment exclusions for the current shipment
    const shipmentId = getEventOrder(event)?.shipmentId;
    const assortmentExclusions =
      shipmentId !== undefined
        ? (assortmentExclusionsByShipmentId.get(shipmentId) ?? [])
        : [];

    const td = {
      area,
      assortmentExclusions,
      key,
      hoursDelayConstraint,
      isFirstEvent,
      isLastEvent,
      isFirstShipmentEvent,
      hasMiddle,
      canPortCallSwapLeft: false,
      canPortCallSwapRight: false,
      index: i,
      arriveInPort: state.arriveInPort,
      start,
      operationsStart: state.currentPortCallOperationsStart,
      calculatedDuration,
      work,
      end,
      laden,
      ordersOnBoard: state.ordersOnBoard,
      amount,
      mass: isDischargeEvent(event) ? -massLoaded : massLoaded,
      volume: isDischargeEvent(event) ? -volumeLoaded : volumeLoaded,
      holdVolume: isDischargeEvent(event) ? -holdVolume : holdVolume,
      deckVolume: isDischargeEvent(event) ? -deckVolume : deckVolume,
      pcs: isDischargeEvent(event) ? -pcs : pcs,
      previousEventStart: prevTd?.start ?? null,
      hoursAtSea,
      channelDurationOverhead,
      calculatedSeaDuration,
      hoursAtPort,
      totalHours: hoursAtSea + hoursAtPort,
      portActivities: msToH(duration),
      hoursWaitingOrRtaBuffer,
      cancellingOrDeliveryViolationVsEta,
      cancellingOrDeliveryViolationVsOperations,
      cancellingOrDeliveryViolationVsEvent,
      physicalTimeConflict: Math.max(0, arriveToEvent - start),
      startOverrideConflict: Math.max(
        0,
        start - (startOverride ?? start) - minimumEventDuration,
      ),
      costs: {
        bunkerGrade: {
          insideEcaData,
          insideEcaDataSecondary,
          outsideEcaData,
          outsideEcaDataSecondary,
          portData: bunkerPortData,
          portDataSecondary: bunkerPortDataSecondary,
          extraConsumptionData,
          extraConsumptionDataSecondary,
          total: bunkerCostTotal,
        },
        tcCostSea: seaCost,
        tcCostPort: portCost,
        tcCost,
        totalCost: total,
        portCostEstimate,
        channelCost,
        cleaningCost,
      },
      totalCO2,
      totalConsumedBunkers,
      usedChannels: routeData?.used_channels ?? [],
      bannedChannelsInRoute,
      distance,
      event,
      availableVolumeCapacity: state.volumeCapacityAvailable,
      availableDeckVolumeCapacity: state.deckVolumeCapacityAvailable,
      availableMassCapacity: state.massCapacityAvailable,
      usedMassCapacity: weightCapacity - state.massCapacityAvailable,
      usedVolumeCapacity: volumeCapacity - state.volumeCapacityAvailable,
      usedDeckVolumeCapacity:
        deckVolumeCapacity - state.deckVolumeCapacityAvailable,
      duration,
      delays: delay,
      signedDelays: signedDelay,
      constrainedDelays: constrainedDelay,
      inputs,
    };

    state.prev = endLocationId;
    state.previousEnd = Math.max(state.previousEnd, end);
    if (isFirstShipmentEvent) {
      state.hasFirstShipmentEvent = true;
    }

    arr[i] = td;
    if (writeTimeDataCache) {
      cacheTimeData(event, prevTd ?? null, [
        td,
        cloneDeriveCapacitiesAndTimesState(state),
      ]);
    }
  }

  if (!skipSwapComputation) {
    // Detect if each location/port call can be swapped to either direction
    applyPortCallSwapInfoToTimeData(arr);
  }

  return {
    timeData: arr,
    totals: {
      totalHours: state.totalHoursAtSea + state.totalHoursAtPort,
      hoursAtSea: state.totalHoursAtSea,
      hoursAtPort: state.totalHoursAtPort,
      ballastDistance: state.ballastDistance,
      distance: state.totalDistance,
      mass: state.totalMass,
      bunkerCost: state.bunkerCost,
      portCost: state.portCostEstimateTotal,
      delays: state.delays,
      constrainedDelays: state.constrainedDelays,
      cost: state.cost,
      tcCost: state.tcCostTotal,
    },
    inputs: {
      vesselId: vessel.id,
      weightCapacity,
      volumeCapacity,
      deckVolumeCapacity,
      tcRate,
    },
  };
};

/**
 * Determine the port type (L/D/M/F) from events in a port call. Returns undefined if no port type is found.
 *
 * Note: Currently we distinguish between loading, discharging, M and bunkering port calls.
 * Other port types are not supported yet.
 * */
function getPortTypeFromEvents(
  events: Events[],
  portCallId: string | number | null | undefined,
): PortType | undefined {
  if (!portCallId) {
    return undefined;
  }

  const portCallEvents = events.filter((e) => e.portCallId === portCallId);

  const hasLoadEvent = portCallEvents.some(isLoadEvent);
  const hasDischargeEvent = portCallEvents.some(isDischargeEvent);

  if (hasLoadEvent && hasDischargeEvent) {
    return PortType.M;
  }
  if (hasLoadEvent) {
    return PortType.L;
  }
  if (hasDischargeEvent) {
    return PortType.D;
  }

  // Check if this is a bunkering port call (has a bunkering single event)
  const hasBunkeringEvent = portCallEvents.some(
    (e) => isSingleEvent(e) && e.event === SingleEventType.BUNKERING,
  );
  if (hasBunkeringEvent) {
    return PortType.F;
  }
  return undefined;
}

const getSortedPriorityKeys = memoize(function getSortedPriorityKeys(
  portCostPriority: PortCostPriority,
) {
  return portCostPriorityKeys.toSorted(
    (a, b) =>
      (portCostPriority[a] ?? Infinity) - (portCostPriority[b] ?? Infinity),
  );
});

/** Get the effective port cost override/default/estimate based on priority */
export function getEffectivePortCost(
  event: Events,
  portCostEstimateMaps: PortCostEstimateMaps,
  portCostPriority: PortCostPriority = DEFAULT_PORT_COST_PRIORITY,
  vessel: Vessel & { name: string },
  locationsById: CalculationConfig['locationsById'],
  portType?: PortType,
) {
  const {
    byName,
    byPrimaryAttributeId,
    latestByPrimaryAttributeId,
    defaultById,
    defaultByTypeId,
    defaultByVesselId,
    locationIdsByName,
  } = portCostEstimateMaps;

  const location = getEventLocation(event, locationsById);
  const locationName = location?.name;
  const locationId = location?.id;
  const primaryVesselAttributeId = vessel.primaryAttributeId;
  let oneTimeOverrideCost = undefined;
  let portCostDefaultId = undefined;
  let useHistoricalPortCost = undefined;
  let useLatestHistoricalPortCost = undefined;
  let useOneTimeOverrideCost = undefined;

  if (isPortCallArrivalEvent(event)) {
    oneTimeOverrideCost = event.oneTimeOverrideCost;
    portCostDefaultId = event.portCostDefaultId;
    useHistoricalPortCost = event.useHistoricalPortCost;
    useLatestHistoricalPortCost = event.useLatestHistoricalPortCost;
    useOneTimeOverrideCost = event.useOneTimeOverrideCost;
  }

  // Handle explicitly selected sources first for performance
  if (useOneTimeOverrideCost && oneTimeOverrideCost != null) {
    return oneTimeOverrideCost;
  }
  if (portCostDefaultId) {
    return defaultById.get(portCostDefaultId) ?? 0;
  }

  const estimate = getHistoricalPortCostEstimates({
    locationName,
    vesselName: vessel.name,
    primaryVesselAttributeId,
    byName,
    byPrimaryAttributeId,
    portType,
  });

  if (useHistoricalPortCost) {
    return estimate?.cost ?? 0;
  }

  if (useLatestHistoricalPortCost && locationName) {
    const latestByAttribute = getLatestHistoricalPortCost(
      locationName,
      primaryVesselAttributeId,
      latestByPrimaryAttributeId,
      portType,
    );
    if (latestByAttribute?.cost !== undefined) {
      return latestByAttribute.cost;
    }
  }

  // NOTE: Early return for integration cost would be here if the data was available. (getIntegrationPortCosts)

  // Fallback to priority-based selection if no source was explicitly chosen
  const costValues: Record<keyof PortCostPriority, number | undefined> = {
    portCostDefault: undefined,
    portCostDefaultMax: undefined,
    portCostHistory: estimate?.cost,
    portCostHistoryLatest: undefined,
    portCostOverride: undefined,
    portCostIntegration: undefined,
  };

  if (oneTimeOverrideCost) {
    costValues.portCostOverride = oneTimeOverrideCost;
  }

  let defaultCost: number | undefined;
  if (primaryVesselAttributeId) {
    const defaultByVesselClassCost = getDefaultPortCostByType(
      defaultByTypeId,
      location.id,
      primaryVesselAttributeId,
      locationIdsByName,
    );
    if (defaultByVesselClassCost) {
      defaultCost = defaultByVesselClassCost.cost;
    }
  }

  const defByNameCost = defaultByVesselId.get(locationId, vessel.id);
  if (defaultCost === undefined && defByNameCost) {
    defaultCost = defByNameCost.cost;
  }

  if (defaultCost === undefined) {
    const typeIds = vessel.vesselAttributesAssociationsByShipId.nodes ?? [];
    for (const type of typeIds) {
      const defByTypeIdCost =
        type?.vesselAttributeId &&
        getDefaultPortCostByType(
          defaultByTypeId,
          location.id,
          type.vesselAttributeId,
          locationIdsByName,
        );
      if (defByTypeIdCost) {
        defaultCost = defByTypeIdCost.cost;
        break;
      }
    }
  }

  if (defaultCost) {
    costValues.portCostDefault = defaultCost;
  }

  // Compute max across ALL default sources at this location (all vessels/types, not just this vessel)
  if (portCostPriority.portCostDefaultMax != null) {
    const locationMaxCost = getMaxDefaultPortCostForLocation(
      locationId,
      portCostEstimateMaps,
    );
    if (locationMaxCost !== undefined) {
      costValues.portCostDefaultMax = locationMaxCost.cost;
    }
  }

  // Sort keys by priority and return the first available value
  const sortedPriorityKeys = getSortedPriorityKeys(portCostPriority);

  for (const key of sortedPriorityKeys) {
    const value = costValues[key];
    if (value != null) {
      return value;
    }
  }

  return 0;
}

function getChannelCost(
  event: Events,
  used_channels: string[],
  laden: boolean,
  carriedAssortmentsByOrderId: DeriveCapacitiesAndTimesState['carriedAssortmentsByOrderId'],
  vessel: Vessel,
  portCostEstimateMaps: PortCostEstimateMaps,
  vesselAttributeById: CalculationConfig['vesselAttributeById'],
  now: number,
) {
  let channelDurationOverhead = 0;

  if (!used_channels?.length) {
    return { channelCost: undefined, channelDurationOverhead };
  }

  const arrivalEvent = isPortCallArrivalEvent(event) ? event : null;

  // All vessel attribute names for matching (checked in order)
  const vesselAttributeNames = vessel.vesselAttributesAssociationsByShipId.nodes
    .map((vaa) => {
      const id = vaa?.vesselAttributeId;
      if (!id) {
        return null;
      }
      return vesselAttributeById.get(id)?.name;
    })
    .filter(isDefined);

  // We use the first carried order's assortment group for channel cost matching
  const firstCarriedOrderAssortmentGroupId =
    mapGetFirstEntry(carriedAssortmentsByOrderId)?.[1]?.assortmentGroupId ??
    null;

  const cargoCondition = laden
    ? ChannelCostCargoCondition.LADEN
    : ChannelCostCargoCondition.BALLAST;

  const baseCriteria: Omit<ChannelCostMatchCriteria, 'channelName'> = {
    vesselId: vessel.id,
    vesselTypeNames:
      vesselAttributeNames.length > 0 ? vesselAttributeNames : null,
    assortmentGroupId: firstCarriedOrderAssortmentGroupId,
    cargoCondition,
  };

  const result = getEffectiveChannelCost({
    channelNames: used_channels,
    oneTimeOverrideChannelCost: arrivalEvent?.oneTimeOverrideChannelCost,
    channelCostDefaultId: arrivalEvent?.channelCostDefaultId,
    channelCostIndex: portCostEstimateMaps.channelCostIndex,
    channelCostRuleById: portCostEstimateMaps.channelById,
    baseCriteria,
  });

  // Only add channel duration overhead if sea duration is not overridden and the event is in future
  const canUseDurationOverhead =
    isPortCallArrivalEvent(event) &&
    !event.seaDurationOverride &&
    event.start >= now;

  if (canUseDurationOverhead) {
    channelDurationOverhead = result.durationOverhead;
  }

  return {
    channelCost: result.cost || undefined,
    channelDurationOverhead,
  };
}

/** Inspects each location in the TimeData sequence and sets canPortCallSwapLeft & canPortCallSwapRight values on all tds at once.
 * Mutates the input.
 * Should be called at the end of DeriveCapacitiesAndTimes on the result.
 *
 * Port call events get flag set to true if ALL individual events inside are eligible to be included in port call swap.
 * Events inside get flag set to true depending on if they themselves are eligible.
 */
export const applyPortCallSwapInfoToTimeData = <EventType extends Events>(
  tds: TimeData<EventType>[],
): void => {
  const tdsByLocation = groupBySequence(tds, (td) =>
    getEventLocationId(td.event),
  );
  tdsByLocation.forEach((locationTds, locationIndex) => {
    const prevLocationEvents = get(tdsByLocation, locationIndex - 1);
    const nextLocationEvents = get(tdsByLocation, locationIndex + 1);

    const tdsByShipment = new Map<
      CargoEvent['order']['shipmentId'],
      TimeData<CargoEvent>[]
    >();

    locationTds.forEach((td) => {
      if (isCargoEventTimeData(td)) {
        const sId = getEventShipmentId(td.event);
        const value = tdsByShipment.get(sId);
        if (value == undefined) {
          tdsByShipment.set(sId, [td]);
        } else {
          value.push(td);
        }
      }
    });

    let allSwapLeftAllowed = true;
    let allSwapRightAllowed = true;
    let isSharedTds = false;

    for (const shipmentTds of tdsByShipment.values()) {
      isSharedTds = shipmentTds.some((td) => td.event.entry.shared);

      const shipmentOrderIds = shipmentTds.map((td) => td.event.order.id);

      const canShipmentSwapLeft =
        !isSharedTds && prevLocationEvents
          ? !prevLocationEvents.some(
              (td) =>
                isCargoEvent(td.event) &&
                (shipmentOrderIds.includes(td.event.order.id) ||
                  td.event.entry.shared),
            )
          : false;

      const canShipmentSwapRight =
        !isSharedTds && nextLocationEvents
          ? !nextLocationEvents.some(
              (td) =>
                isCargoEvent(td.event) &&
                (shipmentOrderIds.includes(td.event.order.id) ||
                  td.event.entry.shared),
            )
          : false;

      allSwapLeftAllowed &&= canShipmentSwapLeft;
      allSwapRightAllowed &&= canShipmentSwapRight;

      for (const td of shipmentTds) {
        td.canPortCallSwapLeft = canShipmentSwapLeft;
        td.canPortCallSwapRight = canShipmentSwapRight;
      }
    }

    if (allSwapLeftAllowed) {
      for (const td of locationTds) {
        if (isPortCallEventTimeData(td)) {
          td.canPortCallSwapLeft = true;
        }
      }
    }
    if (allSwapRightAllowed) {
      for (const td of locationTds) {
        if (isPortCallEventTimeData(td)) {
          td.canPortCallSwapRight = true;
        }
      }
    }
    //   }
  });
};
